/* 
===========================================================
GardeCalculator.jsx

Objectif : Estimer les frais potentiels liés à une demande
de garde d'enfants ou de pension alimentaire au Québec.

Style & Design : Inspirés de DivorceCostEstimator.jsx et
SeparationCalculator.jsx, mais on conserve le contenu
et la logique existants du GardeCalculator.
===========================================================
*/

import React, { useState, useEffect } from "react";
import { Tooltip } from "react-tooltip";
import DashNavbar from "../components/PlumPlusDash/DashNavbar.jsx";
import {
  InformationCircleIcon,
  ArrowLeftCircleIcon,
  ArrowRightCircleIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon,
  ChevronDownIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
} from "recharts";
import { requireAuth } from "../auth"; // Ajuster le chemin si nécessaire

// ──────────────────────────────────────────────────────────
// 1) FRAIS OFFICIELS, FOURCHETTES DE COÛTS, PALETTES DE COULEURS
// ──────────────────────────────────────────────────────────

// Frais de cour officiels
const OFFICIAL_FEES = {
  DEPOT_DEMANDE: 355,
  DEPOT_REPONSE: 177,
  DEMANDE_CONJOINTE: 128,
};

// Fourchettes de coûts
const COST_RANGES = {
  AVOCAT_AMIABLE: { min: 1000, max: 3000 },
  AVOCAT_AMIABLE_PARTAGE: { min: 600, max: 1500 },
  AVOCAT_CONTESTE: { min: 3000, max: 10000 },
  HUISSIER: { min: 50, max: 200 },
  TRADUCTION_PAGE: { min: 50, max: 100 },
};

// Coûts des expertises
const EXPERTISE_COSTS = {
  PSYCHOSOCIALE: { min: 2000, max: 5000 },
  PARENTALE: { min: 1500, max: 4000 },
  LIAISON: { min: 500, max: 1500 },
  IMMEDIAT: { min: 600, max: 1200 },
};

// Règles spéciales (urgences, complexité, etc.)
const SPECIAL_RULES = {
  URGENCE_SURCHARGE: 0.3,  // +30% si urgence
  COMPLEXITE_SURCHARGE: 0.5, // +50% si très complexe
};

// Palettes de couleurs (adaptées pour progress bars, etc.)
const COLORS = {
  primary: {
    light: "#E3F2FD",
    main: "#2196F3",
    dark: "#1565C0",
  },
  secondary: {
    light: "#F3E5F5",
    main: "#9C27B0",
    dark: "#6A1B9A",
  },
  success: {
    light: "#E8F5E9",
    main: "#4CAF50",
    dark: "#2E7D32",
  },
  error: {
    light: "#FFEBEE",
    main: "#F44336",
    dark: "#C62828",
  },
  warning: {
    light: "#FFF8E1",
    main: "#FFC107",
    dark: "#F57F17",
  },
  info: {
    light: "#E1F5FE",
    main: "#03A9F4",
    dark: "#0277BD",
  },
  grey: {
    50: "#FAFAFA",
    100: "#F5F5F5",
    200: "#EEEEEE",
    300: "#E0E0E0",
    400: "#BDBDBD",
    500: "#9E9E9E",
    600: "#757575",
    700: "#616161",
    800: "#424242",
    900: "#212121",
  },
};

// Couleurs pour le graphique en secteurs
const CHART_COLORS = [
  "#2196F3",
  "#9C27B0",
  "#4CAF50",
  "#F44336",
  "#FFC107",
  "#03A9F4",
];

// ──────────────────────────────────────────────────────────
// 2) ÉTAPES DU CALCULATEUR (REPLACED WITH SECTIONS)
// ──────────────────────────────────────────────────────────

const sections = [
  {
    id: "situation",
    title: "Situation initiale",
    icon: "👨‍👩‍👧",
    description:
      "Décrivez votre situation actuelle pour commencer l'estimation.",
    questions: [
      {
        name: "relationStatus",
        type: "radio",
        label: "Quel est votre statut relationnel ?",
        options: [
          { value: "conjointsDeFait", label: "Conjoints de fait" },
          { value: "divorces", label: "Divorcés ou en instance de divorce" },
          { value: "jamaisVecuEnsemble", label: "Parents n'ayant jamais vécu ensemble" },
        ],
        tooltip: "Votre statut détermine certaines procédures légales applicables.",
      },
      {
        name: "isContested",
        type: "radio",
        label: "La garde/pension est-elle contestée ?",
        options: [
          { value: "no", label: "Non (entente amiable possible)" },
          { value: "yes", label: "Oui (désaccord sur la garde, le temps parental ou la pension)" },
        ],
        helpText: "Une contestation implique généralement plus de temps et de frais (avocat, expertise, etc.).",
      },
      {
        name: "filingRole",
        type: "radio",
        label: "Votre rôle dans la procédure :",
        options: [
          { value: "demandeur", label: "Je dépose la requête (demandeur)" },
          { value: "defendeur", label: "Je réponds à la requête (défendeur)" },
          { value: "conjointe", label: "Demande conjointe / entente commune" },
        ],
        tooltip: "Le demandeur supporte habituellement certains frais initiaux (dépôt).",
      },
    ],
  },
  {
    id: "courtFees",
    title: "Frais de cour & Procédure",
    icon: "⚖️",
    description:
      "Déterminez les frais judiciaires initiaux et les éventuelles particularités de la procédure.",
    questions: [
      {
        name: "filingType",
        type: "radio",
        label: "Qui paiera les frais de dépôt à la cour ?",
        options: [
          {
            value: "conjointe",
            label: `Demande conjointe (${OFFICIAL_FEES.DEMANDE_CONJOINTE} $)`,
            showIf: (ans) => ans.isContested === "no" && ans.filingRole === "conjointe",
          },
          {
            value: "demande",
            label: `Je dépose la requête (${OFFICIAL_FEES.DEPOT_DEMANDE} $)`,
            showIf: (ans) => ans.filingRole !== "conjointe",
          },
          {
            value: "reponse",
            label: `Je dépose une réponse (${OFFICIAL_FEES.DEPOT_REPONSE} $)`,
            showIf: (ans) => ans.filingRole !== "conjointe",
          },
          { value: "none", label: "Je ne paierai pas ces frais (0 $)" },
        ],
        tooltip:
          "Les frais de dépôt pour une demande ou une réponse varient selon le type de procédure.",
      },
      {
        name: "needEmergency",
        type: "radio",
        label: "Avez-vous besoin de mesures urgentes ? (provisoires)",
        options: [
          { value: "yes", label: "Oui (ex: demande de sauvegarde, mesures provisoires urgentes)" },
          { value: "no", label: "Non" },
        ],
        helpText:
          "Une procédure urgente permet d'obtenir une décision rapide mais peut augmenter les frais d'avocat.",
      },
      {
        name: "district",
        type: "select",
        label: "District judiciaire du dépôt",
        options: [
          { value: "montreal", label: "Montréal (souvent plus engorgé)" },
          { value: "quebec", label: "Québec" },
          { value: "laval", label: "Laval" },
          { value: "gatineau", label: "Gatineau" },
          { value: "longueuil", label: "Longueuil" },
          { value: "sherbrooke", label: "Sherbrooke" },
          { value: "troisRivieres", label: "Trois-Rivières" },
          { value: "autre", label: "Autre district" },
        ],
        tooltip:
          "Les délais peuvent varier d'un district à l'autre. Montréal est souvent plus lent.",
      },
    ],
  },
  {
    id: "lawyer",
    title: "Avocat",
    icon: "👨‍⚖️",
    description:
      "Précisez si vous comptez faire appel à un avocat pour vous représenter ou vous conseiller.",
    questions: [
      {
        name: "wantLawyer",
        type: "radio",
        label: "Engager un avocat ?",
        options: [
          { value: "true", label: "Oui" },
          { value: "false", label: "Non (0 $)" },
        ],
        tooltip:
          "Un avocat facilite la procédure, mais représente un coût important.",
      },
      {
        name: "lawyerShared",
        type: "radio",
        label: "Partage d'avocat ?",
        options: [
          { value: "yes", label: "Oui (nous utiliserons le même avocat pour rédiger l'entente)" },
          { value: "no", label: "Non (chacun son avocat ou un seul en a)" },
        ],
        showIf: (ans) => ans.wantLawyer === "true" && ans.isContested === "no",
        helpText:
          "Réduit les coûts, mais possible seulement si vous êtes d'accord sur tous les points. L'avocat agira comme rédacteur impartial.",
      },
      {
        name: "complexityLevel",
        type: "radio",
        label: "Complexité de votre dossier :",
        options: [
          { value: "simple", label: "Simple (garde non contestée, peu de litiges)" },
          { value: "moderate", label: "Moyenne (quelques points de désaccord ou calculs de pension)" },
          { value: "complex", label: "Complexe (plusieurs désaccords, enjeux spéciaux)" },
        ],
        showIf: (ans) => ans.wantLawyer === "true",
        tooltip:
          "Plus un dossier est complexe, plus l'avocat passera de temps (frais sup.).",
      },
      {
        name: "lawyerFeeType",
        type: "radio",
        label: "Type de tarification",
        options: [
          { value: "range", label: "Fourchette estimée" },
          { value: "custom", label: "Montant personnalisé" },
        ],
        showIf: (ans) => ans.wantLawyer === "true",
      },
      {
        name: "customFee",
        type: "number",
        label: "Montant personnalisé ($)",
        showIf: (ans) =>
          ans.wantLawyer === "true" && ans.lawyerFeeType === "custom",
      },
    ],
  },
  {
    id: "services",
    title: "Signification et traduction",
    icon: "📋🌐",
    description:
      "Aurez-vous besoin de faire remettre officiellement des documents par huissier ou de faire traduire des preuves?",
    questions: [
      {
        name: "needHuissier",
        type: "radio",
        label: "Besoin d'un huissier ?",
        options: [
          { value: "no", label: "Non (0 $)" },
          { value: "yes", label: "Oui" },
        ],
        tooltip:
          "Un huissier peut être requis pour signifier officiellement certains actes.",
      },
      {
        name: "huissierServices",
        type: "slider",
        label: "Nombre de services requis",
        min: 1,
        max: 10,
        showIf: (ans) => ans.needHuissier === "yes",
        tooltip:
          "Chaque document distinct à signifier compte comme un service.",
      },
      {
        name: "significationLocation",
        type: "radio",
        label: "Localisation de l'autre partie",
        options: [
          { value: "quebec", label: "Au Québec" },
          { value: "canada", label: "Dans un autre province du Canada" },
          { value: "etranger", label: "À l'étranger" },
        ],
        showIf: (ans) => ans.needHuissier === "yes",
        tooltip:
          "Les frais d'huissier augmentent avec la distance / l'étranger.",
      },
      {
        name: "needTranslation",
        type: "radio",
        label: "Avez-vous des documents à traduire ?",
        options: [
          { value: "no", label: "Non (0 $)" },
          { value: "yes", label: "Oui" },
        ],
        tooltip:
          "La traduction assermentée peut être requise si certains documents ne sont pas en français ou en anglais.",
      },
      {
        name: "translationPages",
        type: "slider",
        label: "Nombre de pages à traduire",
        min: 1,
        max: 50,
        showIf: (ans) => ans.needTranslation === "yes",
      },
    ],
  },
  {
    id: "expertise",
    title: "Expertises",
    icon: "🔍",
    description:
      "En cas de désaccord important, une expertise peut être demandée pour éclairer le tribunal.",
    questions: [
      {
        name: "needExpertise",
        type: "radio",
        label: "Besoin d'une expertise ?",
        options: [
          { value: "no", label: "Non (0 $)" },
          { value: "yes", label: "Oui" },
          { value: "unsure", label: "Je ne suis pas sûr(e)" },
        ],
        tooltip:
          "Les experts aident à évaluer la situation familiale, la relation parent-enfant, etc.",
      },
      {
        name: "expertiseType",
        type: "checkbox",
        label: "Types d'expertise potentiellement nécessaires :",
        options: [
          {
            value: "PSYCHOSOCIALE",
            label: `Expertise psychosociale (${EXPERTISE_COSTS.PSYCHOSOCIALE.min}$ - ${EXPERTISE_COSTS.PSYCHOSOCIALE.max}$)`,
            description: "Évaluation globale de la dynamique familiale et des besoins de l'enfant."
          },
          {
            value: "PARENTALE",
            label: `Évaluation des capacités parentales (${EXPERTISE_COSTS.PARENTALE.min}$ - ${EXPERTISE_COSTS.PARENTALE.max}$)`,
            description: "Analyse spécifique des compétences parentales."
          },
        ],
        showIf: (ans) => ans.needExpertise === "yes" || ans.needExpertise === "unsure",
        tooltip: "Cochez toutes les expertises qui pourraient s'appliquer.",
      },
    ],
  },
  {
    id: "summary",
    title: "Résumé et économies",
    icon: "💰",
    description:
      "Voici l'estimation des coûts basée sur vos réponses et des conseils pour la suite.",
    questions: [],
  },
];

// ──────────────────────────────────────────────────────────
// 3) HOOK UTILITAIRE : STOCKAGE LOCAL
// ──────────────────────────────────────────────────────────

function useLocalStorage(key, initialValue) {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const setValue = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error("Error writing to localStorage:", error);
    }
  };

  return [storedValue, setValue];
}

// ──────────────────────────────────────────────────────────
// 4) COMPOSANTS D'AFFICHAGE : RÉPARTITION, HISTORIQUE, ETC.
// ──────────────────────────────────────────────────────────

function CostBreakdownDisplay({ lineItems }) {
  return (
    <div className="w-full space-y-4">
      {lineItems.map((item, index) => (
        <div key={index} className="w-full">
          <div className="flex justify-between mb-1">
            <span className="text-sm font-medium">{item.label}</span>
            <span className="text-sm font-medium">{item.percentage}%</span>
          </div>
          <progress
            className="progress progress-primary w-full"
            value={item.percentage}
            max="100"
          ></progress>
          <div className="text-xs text-gray-500 text-right mt-1">
            {item.min}$ - {item.max}$
          </div>
        </div>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// 5) COMPOSANT PRINCIPAL : GardeCalculator
// ──────────────────────────────────────────────────────────

function GardeCalculator() {
  const [activeSectionId, setActiveSectionId] = useState(sections[0].id);
  const [completedSections, setCompletedSections] = useLocalStorage("gardeCostCompletedSections", {});
  const [answers, setAnswers] = useLocalStorage("gardeCostAnswers", {});
  const [lineItems, setLineItems] = useState([]);
  const [totalCost, setTotalCost] = useState({ min: 0, max: 0 });
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showLegalAidInfo, setShowLegalAidInfo] = useState(true);
  const [showComparison, setShowComparison] = useState(false);
  const [faqOpenIndex, setFaqOpenIndex] = useState(null);

  // Update the progress calculation in the GardeCalculator component
  const progressPercent = Math.round((sections.findIndex(s => s.id === activeSectionId) / (sections.length - 1)) * 100);

  useEffect(() => {
    calculateCosts();
    // Mark section complete automatically
    const currentSection = sections.find(s => s.id === activeSectionId);
    if (currentSection && isSectionComplete(currentSection.id)) {
      setCompletedSections(prev => ({ ...prev, [activeSectionId]: true }));
    }
    // eslint-disable-next-line
  }, [answers, activeSectionId]);

  const handleChange = (name, value) => {
    setAnswers((prev) => {
      const newAnswers = { ...prev, [name]: value };
      try {
        localStorage.setItem("gardeCostAnswers", JSON.stringify(newAnswers));
      } catch (e) {
        console.log("Error saving to localStorage:", e);
      }
      return newAnswers;
    });
  };

  // Navigate to next section
  const handleNextSection = () => {
    const currentIndex = sections.findIndex(s => s.id === activeSectionId);
    if (currentIndex < sections.length - 1) {
       // Mark current section complete before moving
      if (isSectionComplete(activeSectionId)) {
        setCompletedSections(prev => ({ ...prev, [activeSectionId]: true }));
      }
      setActiveSectionId(sections[currentIndex + 1].id);
      window.scrollTo(0, 0); // Scroll to top on section change
    }
  };

  // Navigate to previous section
  const handlePrevSection = () => {
    const currentIndex = sections.findIndex(s => s.id === activeSectionId);
    if (currentIndex > 0) {
      setActiveSectionId(sections[currentIndex - 1].id);
      window.scrollTo(0, 0); // Scroll to top on section change
    }
  };

  // Go to a specific section
  const goToSection = (sectionId) => {
     const targetIndex = sections.findIndex(s => s.id === sectionId);
     const currentIndex = sections.findIndex(s => s.id === activeSectionId);

     // Allow navigation only to already visited or next available section
     let canNavigate = false;
     if (targetIndex <= currentIndex) {
       canNavigate = true; // Can always go back
     } else {
       // Check if all previous sections are complete
       let previousSectionsComplete = true;
       for (let i = 0; i < targetIndex; i++) {
          if (!completedSections[sections[i].id] && sections[i].questions.length > 0) {
             previousSectionsComplete = false;
             break;
           }
       }
       if (previousSectionsComplete) {
         canNavigate = true;
       }
     }

     if (canNavigate) {
       setActiveSectionId(sectionId);
       window.scrollTo(0, 0);
     } else {
       // Optionally show a message indicating sections must be completed first
       console.log("Please complete previous sections first.");
     }
  };

  // Bouton réinitialiser + confirmation
  const handleReset = () => {
    if (showResetConfirm) {
      setAnswers({});
      setCompletedSections({});
      setShowResetConfirm(false);
      setActiveSectionId(sections[0].id);
      window.scrollTo(0, 0);
    } else {
      setShowResetConfirm(true);
      setTimeout(() => setShowResetConfirm(false), 5000);
    }
  };

  // Vérif si toutes les questions d'une étape sont complétées
  const isSectionComplete = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section || section.questions.length === 0) return true; // Summary always complete

    return section.questions.every((q) => {
      if (q.showIf && !q.showIf(answers)) return true;
      const answer = answers[q.name];
        if (q.type === 'checkbox') {
          return answer !== undefined; // Consider answered even if empty
        } else {
          return answer !== undefined && answer !== null && answer !== "";
        }
    });
  };

  // ─────────────────────────────────────────────────────
  // 6) CALCUL DES COÛTS
  // ─────────────────────────────────────────────────────

  const calculateCosts = () => {
    let minSum = 0;
    let maxSum = 0;
    const newLineItems = [];

    // 1. Frais de cour
    if (answers.filingType) {
      let fee = 0;
      switch (answers.filingType) {
        case "conjointe":
          fee = OFFICIAL_FEES.DEMANDE_CONJOINTE;
          break;
        case "demande":
          fee = OFFICIAL_FEES.DEPOT_DEMANDE;
          break;
        case "reponse":
          fee = OFFICIAL_FEES.DEPOT_REPONSE;
          break;
        case "none":
        default:
          fee = 0;
      }
      newLineItems.push({
        label: "Frais de cour",
        min: fee,
        max: fee,
        icon: "⚖️",
        percentage: 0,
      });
      minSum += fee;
      maxSum += fee;
    }

    // 2. Honoraires d'avocat
    if (answers.wantLawyer === "true") {
      let lawyerCostMin, lawyerCostMax;
      if (answers.lawyerFeeType === "custom" && answers.customFee) {
        const custom = Number(answers.customFee);
        lawyerCostMin = custom;
        lawyerCostMax = custom;
      } else {
        const isContested = answers.isContested === "yes";
        const range = isContested
          ? COST_RANGES.AVOCAT_CONTESTE
          : answers.lawyerShared === "yes"
          ? COST_RANGES.AVOCAT_AMIABLE_PARTAGE
          : COST_RANGES.AVOCAT_AMIABLE;

        lawyerCostMin = range.min;
        lawyerCostMax = range.max;

        // Surcoût si complexe
        if (answers.complexityLevel === "complex") {
          lawyerCostMin *= 1 + SPECIAL_RULES.COMPLEXITE_SURCHARGE;
          lawyerCostMax *= 1 + SPECIAL_RULES.COMPLEXITE_SURCHARGE;
        } else if (answers.complexityLevel === "moderate") {
          lawyerCostMin *= 1 + SPECIAL_RULES.COMPLEXITE_SURCHARGE / 2;
          lawyerCostMax *= 1 + SPECIAL_RULES.COMPLEXITE_SURCHARGE / 2;
        }
      }

      // Surcoût si urgence
      if (answers.needEmergency === "yes") {
        lawyerCostMin *= 1 + SPECIAL_RULES.URGENCE_SURCHARGE;
        lawyerCostMax *= 1 + SPECIAL_RULES.URGENCE_SURCHARGE;
      }

      newLineItems.push({
        label: "Honoraires d'avocat",
        min: Math.round(lawyerCostMin),
        max: Math.round(lawyerCostMax),
        icon: "👨‍⚖️",
        percentage: 0,
      });
      minSum += Math.round(lawyerCostMin);
      maxSum += Math.round(lawyerCostMax);
    }

    // 3. Frais d'huissier
    if (answers.needHuissier === "yes" && answers.huissierServices) {
      const services = Number(answers.huissierServices);
      const huissierCost = COST_RANGES.HUISSIER;
      let minHuissier = huissierCost.min * services;
      let maxHuissier = huissierCost.max * services;

      // Ajustement selon la localisation
      if (answers.significationLocation === "canada") {
        minHuissier *= 1.5;
        maxHuissier *= 1.5;
      } else if (answers.significationLocation === "etranger") {
        minHuissier *= 3;
        maxHuissier *= 3;
      }

      newLineItems.push({
        label: `Huissier (${services} service(s))`,
        min: Math.round(minHuissier),
        max: Math.round(maxHuissier),
        icon: "📋",
        percentage: 0,
      });
      minSum += Math.round(minHuissier);
      maxSum += Math.round(maxHuissier);
    }

    // 4. Traductions
    if (answers.needTranslation === "yes" && answers.translationPages) {
      const pages = Number(answers.translationPages);
      const tradCost = COST_RANGES.TRADUCTION_PAGE;
      const minTrad = tradCost.min * pages;
      const maxTrad = tradCost.max * pages;
      newLineItems.push({
        label: `Traductions (${pages} page(s))`,
        min: minTrad,
        max: maxTrad,
        icon: "🌐",
        percentage: 0,
      });
      minSum += minTrad;
      maxSum += maxTrad;
    }

    // 5. Expertises
    if (
      (answers.needExpertise === "yes" || answers.needExpertise === "unsure") &&
      answers.expertiseType &&
      answers.expertiseType.length > 0
    ) {
      answers.expertiseType.forEach((type) => {
        let expertiseCost = { min: 0, max: 0 };
        let label = "";

        switch (type) {
          case "PSYCHOSOCIALE":
            expertiseCost = EXPERTISE_COSTS.PSYCHOSOCIALE;
            label = "Expertise psychosociale";
            break;
          case "PARENTALE":
            expertiseCost = EXPERTISE_COSTS.PARENTALE;
            label = "Évaluation parentale";
            break;
          default:
            break;
        }

        newLineItems.push({
          label,
          min: expertiseCost.min,
          max: expertiseCost.max,
          icon: "🔍",
          percentage: 0,
        });
        minSum += expertiseCost.min;
        maxSum += expertiseCost.max;
      });
    }

    // Calcul du pourcentage de chaque poste
    if (minSum > 0) {
      newLineItems.forEach((item) => {
        item.percentage = Math.round((item.min / minSum) * 100);
      });
    }

    setLineItems(newLineItems);
    setTotalCost({ min: minSum, max: maxSum });
  };

  // Simule le partage
  const shareSummary = () => {
    alert("Cette fonctionnalité n'est pas encore disponible. Partage d'estimation.");
  };

  // ─────────────────────────────────────────────────────
  // 7) FONCTIONS POUR AFFICHER LES DIFFÉRENTS ÉLÉMENTS DU FORMULAIRE
  // ─────────────────────────────────────────────────────

  const renderSlider = (question) => {
    const value = parseInt(answers[question.name]) || question.min;
    return (
      <div className="w-full">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>{question.min}</span>
          <span>{question.max}</span>
        </div>
        <div className="flex items-center gap-4">
          <input
            type="range"
            min={question.min}
            max={question.max}
            value={value}
            onChange={(e) => handleChange(question.name, e.target.value)}
            className="range range-primary w-full"
          />
          <span
            className="bg-primary text-white px-3 py-1 rounded-full w-16 text-center"
            style={{ backgroundColor: COLORS.primary.main }}
          >
            {value}
          </span>
        </div>
      </div>
    );
  };

  // Rendu spécifique par type de question
  const renderQuestion = (question) => {
    // Vérifie la condition d'affichage
    if (question.showIf && !question.showIf(answers)) {
      return null;
    }

    switch (question.type) {
      case "radio":
        return (
          <div key={question.name} className="mb-4">
            <label className="block font-medium text-gray-800 mb-1">
              {question.label}
              {question.tooltip && (
                <span className="inline-block ml-1 text-gray-400">
                  <Tooltip
                    id={`tooltip-${question.name}`}
                    place="bottom"
                    className="z-50 text-xs p-2 bg-gray-900 text-white rounded"
                  />
                  <InformationCircleIcon
                    data-tooltip-id={`tooltip-${question.name}`}
                    data-tooltip-content={question.tooltip}
                    className="w-5 h-5 inline-block cursor-pointer ml-1"
                  />
                </span>
              )}
            </label>
            <div className="space-y-2">
              {question.options.map((opt, i) => {
                // Vérif si l'option elle-même a une condition
                if (opt.showIf && !opt.showIf(answers)) return null;
                return (
                  <label
                    key={i}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name={question.name}
                      value={opt.value}
                      checked={answers[question.name] === opt.value}
                      onChange={(e) => handleChange(question.name, e.target.value)}
                      className="radio radio-primary"
                    />
                    <span className="text-gray-700">{opt.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      case "checkbox":
        return (
          <div key={question.name} className="mb-4">
            <label className="block font-medium text-gray-800 mb-1">
              {question.label}
              {question.tooltip && (
                <span className="inline-block ml-1 text-gray-400">
                  <Tooltip
                    id={`tooltip-${question.name}`}
                    place="bottom"
                    className="z-50 text-xs p-2 bg-gray-900 text-white rounded"
                  />
                  <InformationCircleIcon
                    data-tooltip-id={`tooltip-${question.name}`}
                    data-tooltip-content={question.tooltip}
                    className="w-5 h-5 inline-block cursor-pointer ml-1"
                  />
                </span>
              )}
            </label>
            <div className="space-y-2">
              {question.options.map((opt, i) => (
                <label
                  key={i}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    name={question.name}
                    value={opt.value}
                    checked={answers[question.name]?.includes(opt.value)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const oldVal = answers[question.name] || [];
                      const newVal = checked
                        ? [...oldVal, opt.value]
                        : oldVal.filter((v) => v !== opt.value);
                      handleChange(question.name, newVal);
                    }}
                    className="checkbox checkbox-primary"
                  />
                  <span className="text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        );
      case "select":
        return (
          <div key={question.name} className="mb-4">
            <label className="block font-medium text-gray-800 mb-1">
              {question.label}
              {question.tooltip && (
                <span className="inline-block ml-1 text-gray-400">
                  <Tooltip
                    id={`tooltip-${question.name}`}
                    place="bottom"
                    className="z-50 text-xs p-2 bg-gray-900 text-white rounded"
                  />
                  <InformationCircleIcon
                    data-tooltip-id={`tooltip-${question.name}`}
                    data-tooltip-content={question.tooltip}
                    className="w-5 h-5 inline-block cursor-pointer ml-1"
                  />
                </span>
              )}
            </label>
            <select
              className="select select-bordered w-full"
              value={answers[question.name] || ""}
              onChange={(e) => handleChange(question.name, e.target.value)}
            >
              <option value="">Sélectionnez...</option>
              {question.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        );
      case "slider":
        return (
          <div key={question.name} className="mb-4">
            <label className="block font-medium text-gray-800 mb-1">
              {question.label}
              {question.tooltip && (
                <span className="inline-block ml-1 text-gray-400">
                  <Tooltip
                    id={`tooltip-${question.name}`}
                    place="bottom"
                    className="z-50 text-xs p-2 bg-gray-900 text-white rounded"
                  />
                  <InformationCircleIcon
                    data-tooltip-id={`tooltip-${question.name}`}
                    data-tooltip-content={question.tooltip}
                    className="w-5 h-5 inline-block cursor-pointer ml-1"
                  />
                </span>
              )}
            </label>
            {renderSlider(question)}
          </div>
        );
      case "number":
        return (
          <div key={question.name} className="mb-4">
            <label className="block font-medium text-gray-800 mb-1">
              {question.label}
              {question.tooltip && (
                <span className="inline-block ml-1 text-gray-400">
                  <Tooltip
                    id={`tooltip-${question.name}`}
                    place="bottom"
                    className="z-50 text-xs p-2 bg-gray-900 text-white rounded"
                  />
                  <InformationCircleIcon
                    data-tooltip-id={`tooltip-${question.name}`}
                    data-tooltip-content={question.tooltip}
                    className="w-5 h-5 inline-block cursor-pointer ml-1"
                  />
                </span>
              )}
            </label>
            <div className="flex items-center">
              <input
                type="number"
                className="input input-bordered w-32 mr-2"
                value={answers[question.name] || ""}
                onChange={(e) => handleChange(question.name, e.target.value)}
              />
              <span className="text-gray-700">$</span>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // ─────────────────────────────────────────────────────
  // 8) RENDU DU RÉSUMÉ ET DES ASTUCES/FAQ
  // ─────────────────────────────────────────────────────

  // Astuces pour économiser
  const savingTips = [
    "Considérez la médiation familiale (les 5 premières heures sont gratuites au Québec).",
    "Regroupez vos documents et informations pour réduire le temps d'avocat.",
    "Vérifiez si vous êtes admissible à l'aide juridique (faibles revenus).",
    "Évitez le plus possible la contestation, privilégiez une entente amiable.",
    "Comparez les tarifs de plusieurs avocats et demandez s'ils offrent des forfaits fixes.",
  ];

  // Garde specific saving tips
  const GARDE_SAVING_TIPS = [
    {
      title: "Priorisez la médiation familiale",
      description: "Les 5 premières heures sont gratuites pour les questions de garde/pension. C'est souvent le moyen le plus rapide et économique de trouver une entente.",
      potential_savings: "Coûts d'avocat et de cour évités",
      difficulty: "Facile à Modérée",
      relevance: () => true // Always relevant
    },
    {
      title: "Fixation à l'amiable de la pension alimentaire",
      description: "Utilisez les tables québécoises de fixation et le modèle de formulaire pour déterminer la pension sans passer par le tribunal.",
      potential_savings: "Frais de cour et d'avocat",
      difficulty: "Modérée",
      relevance: (ans) => ans.isContested === 'no'
    },
    {
      title: "Préparez votre dossier",
      description: "Rassemblez tous les documents pertinents (revenus, dépenses des enfants, communications antérieures) pour optimiser le temps avec votre avocat.",
      potential_savings: "500$ - 1500$ en honoraires",
      difficulty: "Facile",
      relevance: (ans) => ans.wantLawyer === 'true'
    },
     {
      title: "Vérifiez l'aide juridique",
      description: "Si vos revenus sont bas, l'aide juridique peut couvrir entièrement vos frais d'avocat et de cour pour la garde/pension.",
      potential_savings: "Totalité des frais potentiels",
      difficulty: "Facile",
      relevance: () => true
    }
  ];

  // FAQ
  const faqItems = [
    {
      question: "Les frais peuvent-ils être récupérés auprès de l'autre partie ?",
      answer:
        "Dans certains cas, le tribunal peut ordonner à la partie perdante de contribuer ou de rembourser une partie des frais, mais ce n'est ni automatique ni garanti.",
    },
    {
      question: "Combien de temps dure la procédure ?",
      answer:
        "Les délais varient selon la complexité, la disponibilité de la cour et le district judiciaire. Cela peut aller de quelques mois à plus d'un an.",
    },
    {
      question: "Puis-je tout faire moi-même, sans avocat ?",
      answer:
        "Oui, en théorie, pour un dossier simple et sans contestation. Toutefois, la représentation par avocat est recommandée dès que la situation est plus complexe.",
    },
  ];

  // Info "Aide juridique" (similaire aux autres exemples)
  const renderLegalAidInfo = () => (
    <div
      className={`bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 transition-all duration-300 ${
        showLegalAidInfo ? "opacity-100" : "opacity-0 h-0 overflow-hidden p-0 m-0"
      }`}
      style={{
        backgroundColor: COLORS.info.light,
        borderLeftColor: COLORS.info.main,
      }}
    >
      <div className="flex justify-between">
        <h3
          className="text-blue-800 font-semibold mb-2"
          style={{ color: COLORS.info.dark }}
        >
          Aide juridique
        </h3>
        <button
          onClick={() => setShowLegalAidInfo(false)}
          className="text-gray-500 hover:text-gray-700"
          aria-label="Fermer"
        >
          ✕
        </button>
      </div>
      <p className="text-sm text-gray-700 mb-2">
        <strong>
          Si vous avez de faibles revenus ou recevez de l'aide sociale,
          vérifiez votre admissibilité à l'aide juridique.
        </strong>{" "}
        Vous pourriez éviter certains frais.{" "}
        <a
          href="https://www.csj.qc.ca/commission-des-services-juridiques/aide-juridique/admissibilite/fr"
          className="text-blue-600 hover:underline ml-1"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: COLORS.primary.main }}
        >
          Commission des services juridiques
        </a>
      </p>
      <p className="text-sm text-gray-700">
        Si l'un de vous bénéficie d'une aide financière de dernier recours,
        avisez votre agent du MTESS pour l'informer de vos démarches.
      </p>
    </div>
  );

  // NEW: Function to render a single section (similar to DivorceCalculator)
  const renderSection = (section) => {
    const isCurrent = section.id === activeSectionId;
    const isCompleted = completedSections[section.id] || false;
    const sectionIndex = sections.findIndex(s => s.id === section.id);
    const isSummary = section.id === 'summary';

     // Determine if the section is accessible
    let isAccessible = false;
     if (sectionIndex === 0) {
         isAccessible = true; // First section always accessible
       } else {
         // Check if all previous sections are complete
         let previousSectionsComplete = true;
         for (let i = 0; i < sectionIndex; i++) {
            if (!completedSections[sections[i].id] && sections[i].questions.length > 0) {
              previousSectionsComplete = false;
              break;
            }
         }
         isAccessible = previousSectionsComplete;
      }

    return (
      <div key={section.id} className={`mb-6 rounded-xl border shadow-sm transition-all duration-300 ${
        isCurrent ? 'border-blue-300 bg-blue-50 shadow-md' : isCompleted ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-white'
      } ${isAccessible ? 'opacity-100' : 'opacity-60 cursor-not-allowed'}`}>
        <button
          className={`w-full p-5 text-left flex justify-between items-center ${isAccessible ? 'cursor-pointer' : 'cursor-not-allowed'}`}
          onClick={() => isAccessible && goToSection(section.id)}
          disabled={!isAccessible}
          aria-expanded={isCurrent}
        >
          <div className="flex items-center">
            <span className={`mr-3 text-xl ${isCompleted && !isCurrent ? 'text-green-600' : isCurrent ? 'text-blue-600' : 'text-slate-500'}`}>
              {section.icon}
            </span>
            <h2 className={`text-lg font-semibold ${isCompleted && !isCurrent ? 'text-green-800' : isCurrent ? 'text-blue-800' : 'text-slate-700'}`}>
              {section.title}
            </h2>
          </div>
          <div className="flex items-center">
            {isCompleted && !isCurrent && (
              <CheckCircleIcon className="w-6 h-6 text-green-500 mr-2" />
            )}
             <span className={`transform transition-transform duration-200 ${isCurrent ? 'rotate-180' : 'rotate-0'}`}>
               {isAccessible ? <ChevronDownIcon className="w-5 h-5 text-slate-500" /> : <LockClosedIcon className="w-5 h-5 text-slate-400" /> }
             </span>
          </div>
        </button>

        {/* Collapsible Content */}
        {isCurrent && (
          <div className="px-5 pb-6 pt-2 border-t border-slate-200">
             {isSummary ? (
               // --- Render Summary Content --- 
               renderCostSummary()
             ) : (
               // --- Render Question Section Content --- 
               <>
                 <p className="text-sm text-slate-600 mb-6">{section.description}</p>
                 <div className="space-y-6"> 
                  {section.questions.map((question, qIndex) => {
                     if (question.showIf && !question.showIf(answers)) return null;

                     return (
                        <div key={qIndex} className="p-4 rounded-lg border border-slate-200 bg-white shadow-sm">
                           <div className="flex items-start justify-between gap-2">
                             <label className="font-medium text-slate-800 flex-1">
                               {question.label}
                             </label>
                             {question.helpText && (
                               <Tooltip id={`tooltip-${question.name}`}>
                                 <button
                                   type="button"
                                   className="text-blue-500 hover:text-blue-700"
                                   data-tooltip-id={`tooltip-${question.name}`}
                                   data-tooltip-content={question.helpText}
                                 >
                                   <InformationCircleIcon className="w-5 h-5" />
                                 </button>
                               </Tooltip>
                             )}
                           </div>

                           {/* Question inputs based on type */}
                           <div className="mt-3 space-y-2">
                              {question.type === "radio" && (
                                <div className="space-y-2">
                                  {question.options.map((opt, i) => {
                                    if (opt.showIf && !opt.showIf(answers)) return null;
                                    const isChecked = answers[question.name] === opt.value;
                                    return (
                                      <label
                                        key={i}
                                        className={`cursor-pointer flex items-start gap-3 p-3 rounded-md border transition-colors ${
                                          isChecked ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'
                                        }`}
                                      >
                                        <input
                                          type="radio"
                                          name={question.name}
                                          value={opt.value}
                                          checked={isChecked}
                                          onChange={(e) => handleChange(question.name, e.target.value)}
                                          className="radio radio-primary mt-1 flex-shrink-0"
                                        />
                                        <span className="flex-1">
                                          <span className="text-slate-700 block">{opt.label}</span>
                                          {opt.helpText && <span className="text-xs text-slate-500 block mt-1">{opt.helpText}</span>}
                                        </span>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}

                              {question.type === "slider" && (
                                <div className="w-full">
                                  <div className="flex items-center gap-4">
                                    <input
                                      type="range"
                                      min={question.min}
                                      max={question.max}
                                      value={answers[question.name] || question.min}
                                      onChange={(e) => handleChange(question.name, e.target.value)}
                                      className="range range-primary w-full"
                                    />
                                    <span className="bg-blue-100 text-blue-700 font-medium px-3 py-1 rounded-full w-16 text-center">
                                      {answers[question.name] || question.min}
                                    </span>
                                  </div>
                                   <div className="flex justify-between text-xs text-slate-500 mt-1 px-1">
                                    <span>{question.min}</span>
                                    <span>{question.max}</span>
                                  </div>
                                </div>
                              )}

                               {question.type === "checkbox" && (
                                  <div className="space-y-3">
                                    {question.options.map((opt, i) => {
                                       if (opt.showIf && !opt.showIf(answers)) return null;
                                       const isChecked = answers[question.name]?.includes(opt.value) || false;
                                       return (
                                          <div key={i} className={`rounded-lg border p-4 transition-colors ${isChecked ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                                            <label className="flex items-start gap-3 cursor-pointer">
                                              <input
                                                type="checkbox"
                                                value={opt.value}
                                                checked={isChecked}
                                                onChange={(e) => {
                                                  const currentValues = answers[question.name] || [];
                                                  const newValues = e.target.checked
                                                    ? [...currentValues, opt.value]
                                                    : currentValues.filter(v => v !== opt.value);
                                                  handleChange(question.name, newValues);
                                                }}
                                                className="mt-1 checkbox checkbox-primary flex-shrink-0"
                                              />
                                              <div className="flex-1">
                                                <div className="font-medium text-slate-700">{opt.label}</div>
                                                {opt.description && (
                                                  <p className="text-sm text-slate-500 mt-1">{opt.description}</p>
                                                )}
                                              </div>
                                            </label>
                                          </div>
                                       )
                                    })}
                                  </div>
                               )}

                              {question.type === "select" && (
                                <select
                                  className="select select-bordered w-full"
                                  value={answers[question.name] || ""}
                                  onChange={(e) => handleChange(question.name, e.target.value)}
                                >
                                  <option value="" disabled>Sélectionnez...</option>
                                  {question.options.map((o) => (
                                    <option key={o.value} value={o.value}>
                                      {o.label}
                                    </option>
                                  ))}
                                </select>
                              )}

                              {question.type === "number" && (
                                 <div className="relative mt-1 rounded-md shadow-sm w-full max-w-xs">
                                     <input
                                         type="number"
                                         value={answers[question.name] || ""}
                                         onChange={(e) => handleChange(question.name, e.target.value)}
                                         className="input input-bordered w-full pr-12"
                                         placeholder="0"
                                     />
                                     <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                         <span className="text-gray-500 sm:text-sm">$
                                         </span>
                                     </div>
                                 </div>
                              )}
                           </div>
                        </div>
                     );
                   })} 
                  </div>
                  {/* Navigation Buttons */}
                  <div className="mt-8 flex justify-between items-center pt-4 border-t border-slate-200">
                    <div> 
                      {sectionIndex > 0 && (
                        <button
                          onClick={handlePrevSection}
                          className="btn btn-outline btn-primary btn-sm gap-1"
                        >
                          <ArrowLeftCircleIcon className="w-4 h-4" />
                          Précédent
                        </button>
                      )}
                    </div>
                    <div> 
                      {sectionIndex < sections.length - 1 && (
                        <button
                          onClick={handleNextSection}
                          className="btn btn-primary btn-sm gap-1"
                          disabled={!isSectionComplete(section.id)}
                        >
                          { isSectionComplete(section.id) ? 'Suivant' : 'Complétez la section'}
                          <ArrowRightCircleIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
          </div>
        )}
      </div>
    );
  };

  // Affichage final : résumé avec détails
  const renderCostSummary = () => {
    // Filter tips specifically for Garde scenarios
    const relevantTips = GARDE_SAVING_TIPS.filter(tip => tip.relevance(answers));

    return (
      <div className="space-y-8">
        {/* Top Summary: Total Cost & Key Factors */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="card bg-gradient-to-br from-blue-500 to-blue-700 text-white p-6 rounded-xl shadow-lg">
            <h3 className="font-semibold mb-3 text-lg flex items-center opacity-90">
              <span className="mr-2">💰</span> Estimation Totale (Garde)
            </h3>
            <p className="text-4xl font-bold">
              {totalCost.min.toFixed(0)}$ - {totalCost.max.toFixed(0)}$
            </p>
            <p className="opacity-80 mt-1">Coût total estimé de la procédure</p>
          </div>
          <div className="card bg-white border border-slate-200 p-6 rounded-xl shadow">
            <h3 className="font-semibold text-slate-700 mb-3 flex items-center">
              <span className="mr-2">📊</span> Facteurs Clés
            </h3>
             <ul className="space-y-2 text-sm">
              <li className="flex justify-between"><span>Statut:</span> <span className="font-medium">{answers.relationStatus || 'N/A'}</span></li>
              <li className="flex justify-between"><span>Type:</span> <span className="font-medium">{answers.isContested === 'yes' ? 'Contesté' : 'À l\'amiable'}</span></li>
              <li className="flex justify-between"><span>Complexité:</span> <span className="font-medium">{answers.complexityLevel || 'Simple'}</span></li>
              <li className="flex justify-between"><span>Avocat:</span> <span className="font-medium">{answers.wantLawyer === 'true' ? 'Oui' : 'Non'}</span></li>
              {/* Add more relevant factors */}
             </ul>
          </div>
        </div>

        {/* Detailed Cost Breakdown Card - Styled like DivorceCalc */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-blue-700 mb-4 flex items-center gap-2">
            <span className="bg-blue-50 p-2 rounded-full">📊</span>
            Détail des coûts
          </h2>
          {lineItems.length > 0 ? (
            <CostBreakdownDisplay lineItems={lineItems} />
          ) : (
            <div className="flex flex-col items-center justify-center h-32 bg-slate-50 rounded-lg">
              <ExclamationCircleIcon className="w-12 h-12 text-slate-300 mb-2" />
              <p className="text-slate-500 italic">Aucune donnée disponible</p>
            </div>
          )}
        </div>

        {/* Contextual Saving Tips Card - Styled like DivorceCalc */}
        <div className="bg-green-50 rounded-xl border border-green-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-green-700 mb-4 flex items-center gap-2">
            <span className="bg-green-100 p-2 rounded-full">💡</span>
            Astuces personnalisées pour économiser (Garde)
          </h2>
          <div className="space-y-4">
            {relevantTips.slice(0, 3).map((tip, index) => (
              <div key={index} className="flex items-start gap-4 bg-white p-4 rounded-lg border border-green-100 shadow-sm">
                <div className="bg-green-100 p-2 rounded-full flex-shrink-0 mt-1">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-green-800">{tip.title}</h4>
                  <p className="text-green-700 text-sm mt-1">{tip.description}</p>
                  {tip.potential_savings && <p className="text-xs text-green-600 font-medium mt-1">Économie potentielle: {tip.potential_savings}</p>}
                </div>
              </div>
            ))}
            {relevantTips.length === 0 && <p className="text-green-700 italic text-sm">Aucune astuce spécifique pour le moment.</p>}
          </div>
        </div>

        {/* Next Steps Card - Styled like DivorceCalc */}
        <div className="bg-blue-50 rounded-xl border border-blue-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-blue-700 mb-4 flex items-center gap-2">
            <span className="bg-blue-100 p-2 rounded-full">🚀</span>
            Prochaines étapes suggérées
          </h2>
          <div className="space-y-3 text-sm">
             <p className="text-blue-800">Cette estimation est un guide. Voici des actions utiles :</p>
            <ul className="list-disc list-inside space-y-2 pl-2 text-blue-700">
              <li><a href="#" className="font-medium hover:underline">Explorer la médiation familiale</a>, surtout si des désaccords existent.</li>
              <li><a href="/orientation/aide-juridique" className="font-medium hover:underline">Vérifier votre admissibilité à l'aide juridique</a>.</li>
              <li><a href="/guides/garde" className="font-medium hover:underline">Consulter notre guide sur la garde d'enfants</a> pour plus de détails.</li>
              <li>Utiliser le <a href="#" className="font-medium hover:underline">simulateur de pension alimentaire du gouvernement</a> pour une estimation.</li>
              {answers.wantLawyer === 'true' && <li><a href="/orientation/cabinets" className="font-medium hover:underline">Contacter des avocats spécialisés en droit de la famille</a>.</li>}
            </ul>
            <p className="text-xs text-blue-600 pt-3 border-t border-blue-100 mt-4">Consultez un professionnel pour des conseils personnalisés.</p>
          </div>
        </div>

        {/* FAQ Section Card - Styled like DivorceCalc */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-blue-700 mb-4 flex items-center gap-2">
            <span className="bg-blue-50 p-2 rounded-full">❓</span>
            Questions fréquentes (Garde)
          </h2>
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="border-b border-slate-100 last:border-0 pb-4 last:pb-0">
                <button
                  className="w-full text-left font-medium text-slate-700 flex items-center justify-between p-2 hover:bg-slate-50 rounded transition-colors"
                  onClick={() => setFaqOpenIndex(faqOpenIndex === index ? null : index)}
                >
                  <span>{item.question}</span>
                  <span className="text-blue-500">{faqOpenIndex === index ? '−' : '+'}</span>
                </button>
                {faqOpenIndex === index && (
                  <p className="mt-2 text-slate-600 text-sm p-2">{item.answer}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Reset button - Styled like DivorceCalc */}
         <div className="px-4 mt-8 flex justify-center">
          <button
            onClick={handleReset}
            className={`btn ${showResetConfirm ? 'btn-error' : 'btn-outline'} btn-sm hover:bg-red-600 transition-colors gap-2`}
          >
            <ArrowPathIcon className="w-4 h-4" />
            {showResetConfirm ? 'Confirmer la réinitialisation' : 'Réinitialiser'}
          </button>
        </div>

      </div>
    );
  };

  // ─────────────────────────────────────────────────────
  // 9) RENDU FINAL
  // ─────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto pb-12 px-4">

        {/* Added Standard Header Section */}
        <div className="px-4 md:px-6 py-8 mt-6 bg-gradient-to-r from-green-50 to-lime-50 rounded-xl border border-green-100 shadow-sm mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-green-700 mb-3 text-center">
            <span className="inline-block h-1 bg-lime-400 w-8 mr-2 rounded-full align-middle"></span>
            Calculateur des Coûts (Procédure de Garde d'Enfant)
            <span className="inline-block h-1 bg-lime-400 w-8 ml-2 rounded-full align-middle"></span>
          </h1>
          <p className="text-center text-green-600 max-w-3xl mx-auto">
            Estimez les coûts potentiels de votre procédure de garde d'enfant selon votre situation personnelle.
          </p>
        </div>

        {/* Top Summary Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
            <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">💰</span>
              Estimation des coûts
            </h3>
            <div className="flex items-center justify-center h-16">
              {totalCost.min === 0 && totalCost.max === 0 ? (
                <p className="text-slate-500 text-center text-sm">
                  Complétez le questionnaire pour voir l'estimation
                </p>
              ) : (
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-700">
                    {totalCost.min.toFixed(0)}$ - {totalCost.max.toFixed(0)}$
                  </p>
                  <p className="text-sm text-slate-600">coût total estimé</p>
                </div>
              )}
            </div>
          </div>

          {/* Card 2: Progression */}
          <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
            <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">📊</span>
              Progression du questionnaire
            </h3>
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-semibold">{progressPercent.toFixed(0)}% complété</span>
                <span className="text-slate-500 text-xs">
                  {sections.findIndex(s => s.id === activeSectionId) + 1} / {sections.length} sections
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-3">
                <div
                  className="bg-blue-500 h-3 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Legal Aid Info */}
        {renderLegalAidInfo()}

        {/* Main questionnaire section using renderSection */}
        <div className="relative mb-8 px-4">
          <div className="absolute bg-slate-200 w-[3px] h-full left-4 md:left-6 top-0 z-0" />
          <div className="space-y-6">
            {sections.map(renderSection)}
          </div>
        </div>

      </div>
    </div>
  );
}

export const Component = requireAuth(GardeCalculator);
export default GardeCalculator;
